import isMobilePhone from 'validator/lib/isMobilePhone';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';
import isNumeric from 'validator/lib/isNumeric';
import isFloat from 'validator/lib/isFloat';

import { isEmpty } from './general';
import { isValidDate, isLegalAgeForAlcohol } from './date';
import { isStateAbbrev, isValidUSZip, checkContainsPOBox } from './address';

// Returns an error enum if the error case is true.
function enumIfError(errorCase: boolean, errorEnum: string) {
  return errorCase ? errorEnum : '';
}

// General text validators for a simple input value.
export const universalTextValidators = {
  required: input => {
    return enumIfError(isEmpty(input), 'REQUIRED_FIELD');
  },
  phoneNumber: input => {
    // Eventually, may need to add other locales.
    return enumIfError(!isMobilePhone(input, 'en-US'), 'INVALID_PHONE');
  },
  isNumeric: input => {
    return enumIfError(!isNumeric(input), 'INVALID_NUMBER');
  },
  isFloatOrInt: input => {
    return enumIfError(!isNumeric(input) && !isFloat(input), 'INVALID_NUMBER');
  },
  isPositive: input => {
    return enumIfError(parseFloat(input, 10) < 0, 'INVALID_POSITIVE_NUMBER');
  },
  email: input => {
    return enumIfError(!isEmail(input), 'INVALID_EMAIL');
  },
  noPOBox: input => {
    return enumIfError(checkContainsPOBox(input), 'POBOX_ERROR');
  },
  stateAbbrev: input => {
    return enumIfError(!isStateAbbrev(input), 'INVALID_STATE');
  },
  minLength: (input, min) => {
    return enumIfError(!isLength(input, { min }), 'MIN_LENGTH_ERROR');
  },
  maxLength: (input, max) => {
    return enumIfError(!isLength(input, { min: 0, max }), 'MAX_LENGTH_ERROR');
  },
  zip: input => {
    return enumIfError(!isValidUSZip(input), 'INVALID_ZIP');
  },
};

// Date validators. Different input requirements than universalTextValidators.
export const dateValidators = {
  required: ({ year, month, day }) => {
    const isError = isEmpty(year) && isEmpty(month) && isEmpty(day);
    return enumIfError(isError, 'REQUIRED_FIELD');
  },
  validDate: ({ year, month, day }) => {
    const isValid = isValidDate({ year, month, day });
    return enumIfError(!isValid, 'INVALID_DATE');
  },
  legalAgeForAlcohol: ({ year, month, day }) => {
    const isValid = isLegalAgeForAlcohol({ year, month, day });
    return enumIfError(!isValid, 'ALCOHOL_AGE_ERROR');
  },
};

// Get the appropriate validators for the input type.
export function getValidators(componentType) {
  if (componentType === 'DateInput') {
    return dateValidators;
  }
  return universalTextValidators;
}

// Check if the input is empty, which may require different logic based on input type.
// Called in TextInputValidator.
export function inputIsEmpty(componentType, input) {
  if (componentType === 'DateInput') {
    const { month, day, year } = input;
    return isEmpty(year) && isEmpty(month) && isEmpty(day);
  }
  return isEmpty(input);
}
